import React from "react";
import type { Props as DefaultLegendContentProps } from "recharts/types/component/DefaultLegendContent";

import { Metric } from "~/models/metric";
import { cx } from "~/tools/class-variance";

import { Link } from "../../react/link";
import type { TimeSeries } from "../time-series";

import { LegendContentList, type LegendContentListProps } from "./_legend-content-list";
import type { LegendContentMode } from "./_legend-content-mode";
import { LegendContentTable, type LegendContentTableProps } from "./_legend-content-table";
import type { LegendEntry } from "./_legend-entry";

const CHART_LEGEND_LINE_HEIGHT = 14;
const CHART_LEGEND_SPACE_Y_PX = 4;
const CHART_LEGEND_ROW_HEIGHT = 24;

function getMaxHeightPx(mode: LegendContentMode, maxLines: number): number {
	if (mode === "table") {
		// table head counts as extra one
		return (1 + maxLines) * CHART_LEGEND_ROW_HEIGHT;
	}

	return maxLines * (CHART_LEGEND_LINE_HEIGHT + CHART_LEGEND_SPACE_Y_PX) - CHART_LEGEND_SPACE_Y_PX;
}

export interface LegendContentProps
	extends Omit<DefaultLegendContentProps, "points" | "onMouseEnter">,
		Omit<LegendContentTableProps, "entries">,
		Omit<LegendContentListProps, "entries"> {
	className?: string;
	mode: LegendContentMode;
	totalTimeSeriesCount: number;
	maxLines: undefined | number;
	timeSeries: ReadonlyArray<TimeSeries>;
	onMouseEnter: VoidFunction;
	onShowAllTimeSeries: undefined | VoidFunction;
}

export const LegendContent: React.FC<LegendContentProps> = ({
	className,
	unit,
	payload,
	mode,
	totalTimeSeriesCount,
	maxLines,
	timeSeries,
	points,
	columns,
	state,
	onMouseEnter,
	onShowAllTimeSeries,
}) => {
	const invisibleTimeSeriesCount = payload == null ? 0 : totalTimeSeriesCount - payload.length;

	const entries = React.useMemo(() => {
		if (payload == null) {
			return [];
		}

		const componentIdentifiers = timeSeries.map(({ metric, componentIdentifier }) =>
			Metric.toComponentIdentifier(componentIdentifier, metric),
		);

		return payload.map<LegendEntry>(({ value = "", color, inactive }, index) => ({
			index,
			label: value,
			active: !inactive,
			color,
			componentIdentifier: componentIdentifiers.at(index),
		}));
	}, [payload, timeSeries]);

	return (
		<div
			className={cx("max-h-full w-full overflow-y-auto bg-white text-xs/3.5", className)}
			onMouseEnter={onMouseEnter}
			style={{
				maxHeight: maxLines == null ? undefined : getMaxHeightPx(mode, maxLines),
			}}
		>
			{mode === "list" ? (
				<LegendContentList entries={entries} state={state} />
			) : (
				<LegendContentTable columns={columns} entries={entries} points={points} state={state} unit={unit} />
			)}

			{invisibleTimeSeriesCount > 0 && onShowAllTimeSeries != null && (
				<Link
					className="mt-1 text-gray-700"
					el="button"
					onClick={onShowAllTimeSeries}
					// overflow-hidden makes the button scrollHeight == clientHeight
					theme="secondary"
					type="button"
				>
					+{invisibleTimeSeriesCount} more
				</Link>
			)}
		</div>
	);
};
